/**
 * Meshtastic BLE Constants
 *
 * UUIDs and constants for Meshtastic BLE protocol.
 * Reference: https://meshtastic.org/docs/development/device/client-api
 *
 * ⚠️ EXPERIMENTAL: This integration is under active development.
 */

// ============= BLE Service & Characteristic UUIDs =============

/**
 * Meshtastic BLE Service UUID
 * All Meshtastic devices advertise this service
 */
export const MESHTASTIC_SERVICE_UUID = '6ba1b218-15a8-461f-9fa8-5dcae273eafd'

/**
 * ToRadio Characteristic - Write to send data to the device
 * Supports Write and Write Without Response
 */
export const TORADIO_CHARACTERISTIC_UUID = 'f75c76d2-129e-4dad-a1dd-7866124401e7'

/**
 * FromRadio Characteristic - Read/Notify to receive data from the device
 * Supports Read and Notify
 */
export const FROMRADIO_CHARACTERISTIC_UUID = 'ed9da18c-a800-4f66-a670-aa7547e34453'

/**
 * FromNum Characteristic - Notify when new data is available
 * Provides a packet counter that increments with each new packet
 */
export const FROMNUM_CHARACTERISTIC_UUID = 'ed9da18c-a800-4f66-a670-aa7547e34454'

// ============= Protocol Constants =============

/**
 * Maximum packet size for BLE writes
 * Meshtastic uses 512 byte MTU by default
 */
export const MESHTASTIC_MTU = 512

/**
 * Maximum payload size for LoRa packets
 * Depends on spreading factor, typically 230-250 bytes
 */
export const MAX_LORA_PAYLOAD_SIZE = 230

/**
 * Header size for Meshtastic packets
 */
export const PACKET_HEADER_SIZE = 16

/**
 * Maximum text message length
 */
export const MAX_TEXT_MESSAGE_LENGTH = 228

/**
 * Broadcast address (send to all nodes)
 */
export const BROADCAST_ADDR = 0xffffffff

/**
 * Default hop limit for packets
 */
export const DEFAULT_HOP_LIMIT = 3

/**
 * Configuration request ID used when requesting device config
 */
export const CONFIG_REQUEST_ID = 0x12345678

// ============= Port Numbers =============

/**
 * Meshtastic application port numbers
 * Used to identify the type of payload in a packet
 */
export const PORTNUM = {
  UNKNOWN_APP: 0,
  TEXT_MESSAGE_APP: 1,
  REMOTE_HARDWARE_APP: 2,
  POSITION_APP: 3,
  NODEINFO_APP: 4,
  ROUTING_APP: 5,
  ADMIN_APP: 6,
  TEXT_MESSAGE_COMPRESSED_APP: 7,
  WAYPOINT_APP: 8,
  AUDIO_APP: 9,
  DETECTION_SENSOR_APP: 10,
  REPLY_APP: 32,
  IP_TUNNEL_APP: 33,
  PAXCOUNTER_APP: 34,
  SERIAL_APP: 64,
  STORE_FORWARD_APP: 65,
  RANGE_TEST_APP: 66,
  TELEMETRY_APP: 67,
  ZPS_APP: 68,
  SIMULATOR_APP: 69,
  TRACEROUTE_APP: 70,
  NEIGHBORINFO_APP: 71,
  ATAK_PLUGIN: 72,
  MAP_REPORT_APP: 73,
  /** Private app for custom applications like CrowdTalkie */
  PRIVATE_APP: 256,
  ATAK_FORWARDER: 257,
  MAX: 511,
} as const

// ============= Priority Levels =============

export const PRIORITY = {
  UNSET: 0,
  MIN: 1,
  BACKGROUND: 10,
  DEFAULT: 64,
  RELIABLE: 70,
  ACK: 120,
  MAX: 127,
} as const

// ============= Hardware Models =============

export const HARDWARE_MODEL = {
  UNSET: 0,
  TLORA_V2: 1,
  TLORA_V1: 2,
  TLORA_V2_1_1P6: 3,
  TBEAM: 4,
  HELTEC_V2_0: 5,
  TBEAM_V0P7: 6,
  T_ECHO: 7,
  TLORA_V1_1P3: 8,
  RAK4631: 9,
  HELTEC_V2_1: 10,
  HELTEC_V1: 11,
  LILYGO_TBEAM_S3_CORE: 12,
  RAK11200: 13,
  NANO_G1: 14,
  TLORA_V2_1_1P8: 15,
  TLORA_T3_S3: 16,
  NANO_G1_EXPLORER: 17,
  NANO_G2_ULTRA: 18,
  HELTEC_V3: 43,
  HELTEC_WSL_V3: 44,
  HELTEC_WIRELESS_TRACKER: 51,
  HELTEC_WIRELESS_PAPER: 52,
  T_DECK: 53,
  T_WATCH_S3: 54,
} as const

// ============= Region Codes =============

export const REGION_CODE = {
  UNSET: 0,
  US: 1,
  EU_433: 2,
  EU_868: 3,
  CN: 4,
  JP: 5,
  ANZ: 6,
  KR: 7,
  TW: 8,
  RU: 9,
  IN: 10,
  NZ_865: 11,
  TH: 12,
  LORA_24: 13,
  UA_433: 14,
  UA_868: 15,
  MY_433: 16,
  MY_919: 17,
  SG_923: 18,
} as const

// ============= Modem Presets =============

export const MODEM_PRESET = {
  LONG_FAST: 0,
  LONG_SLOW: 1,
  VERY_LONG_SLOW: 2,
  MEDIUM_SLOW: 3,
  MEDIUM_FAST: 4,
  SHORT_SLOW: 5,
  SHORT_FAST: 6,
  LONG_MODERATE: 7,
  SHORT_TURBO: 8,
} as const

// ============= Channel Roles =============

export const CHANNEL_ROLE = {
  DISABLED: 0,
  PRIMARY: 1,
  SECONDARY: 2,
} as const

// ============= Default Channel Settings =============

/**
 * Default PSK for the default channel (all zeros = no encryption)
 */
export const DEFAULT_PSK = new Uint8Array(0)

/**
 * Simple1 default key (well-known, not secure)
 * AQ== in base64
 */
export const SIMPLE1_PSK = new Uint8Array([1])

/**
 * CrowdTalkie private app identifier
 * Used to identify CrowdTalkie messages on the mesh
 */
export const CROWDTALKIE_APP_ID = 'CT01'

/**
 * CrowdTalkie message type markers
 */
export const CROWDTALKIE_MESSAGE_TYPE = {
  TEXT: 0x01,
  STATE_SYNC: 0x02,
  POI: 0x03,
  PING: 0x04,
  PONG: 0x05,
} as const

// ============= Timing Constants =============

/**
 * Reconnection delay in milliseconds
 */
export const RECONNECT_DELAY_MS = 3000

/**
 * Maximum reconnection attempts before giving up
 */
export const MAX_RECONNECT_ATTEMPTS = 5

/**
 * Timeout for BLE operations in milliseconds
 */
export const BLE_OPERATION_TIMEOUT_MS = 10000

/**
 * Heartbeat interval to keep connection alive
 */
export const HEARTBEAT_INTERVAL_MS = 30000

/**
 * Configuration read timeout
 */
export const CONFIG_TIMEOUT_MS = 30000

/**
 * Delay between reading FromRadio packets
 */
export const READ_LOOP_DELAY_MS = 100

// ============= Display Names =============

export const HARDWARE_MODEL_NAMES: Record<number, string> = {
  [HARDWARE_MODEL.UNSET]: 'Unknown',
  [HARDWARE_MODEL.TLORA_V2]: 'LILYGO T-LoRa V2',
  [HARDWARE_MODEL.TLORA_V1]: 'LILYGO T-LoRa V1',
  [HARDWARE_MODEL.TLORA_V2_1_1P6]: 'LILYGO T-LoRa V2.1-1.6',
  [HARDWARE_MODEL.TBEAM]: 'LILYGO T-Beam',
  [HARDWARE_MODEL.HELTEC_V2_0]: 'Heltec V2.0',
  [HARDWARE_MODEL.HELTEC_V2_1]: 'Heltec V2.1',
  [HARDWARE_MODEL.HELTEC_V1]: 'Heltec V1',
  [HARDWARE_MODEL.HELTEC_V3]: 'Heltec V3',
  [HARDWARE_MODEL.HELTEC_WSL_V3]: 'Heltec Wireless Stick Lite V3',
  [HARDWARE_MODEL.HELTEC_WIRELESS_TRACKER]: 'Heltec Wireless Tracker',
  [HARDWARE_MODEL.HELTEC_WIRELESS_PAPER]: 'Heltec Wireless Paper',
  [HARDWARE_MODEL.RAK4631]: 'RAK WisBlock 4631',
  [HARDWARE_MODEL.RAK11200]: 'RAK WisBlock 11200',
  [HARDWARE_MODEL.LILYGO_TBEAM_S3_CORE]: 'LILYGO T-Beam S3 Core',
  [HARDWARE_MODEL.T_ECHO]: 'LILYGO T-Echo',
  [HARDWARE_MODEL.T_DECK]: 'LILYGO T-Deck',
  [HARDWARE_MODEL.T_WATCH_S3]: 'LILYGO T-Watch S3',
  [HARDWARE_MODEL.NANO_G1]: 'Nano G1',
  [HARDWARE_MODEL.NANO_G1_EXPLORER]: 'Nano G1 Explorer',
  [HARDWARE_MODEL.NANO_G2_ULTRA]: 'Nano G2 Ultra',
}

export const REGION_NAMES: Record<number, string> = {
  [REGION_CODE.UNSET]: 'Unset',
  [REGION_CODE.US]: 'United States (915 MHz)',
  [REGION_CODE.EU_433]: 'Europe 433 MHz',
  [REGION_CODE.EU_868]: 'Europe 868 MHz',
  [REGION_CODE.CN]: 'China',
  [REGION_CODE.JP]: 'Japan',
  [REGION_CODE.ANZ]: 'Australia/New Zealand',
  [REGION_CODE.KR]: 'Korea',
  [REGION_CODE.TW]: 'Taiwan',
  [REGION_CODE.RU]: 'Russia',
  [REGION_CODE.IN]: 'India',
  [REGION_CODE.NZ_865]: 'New Zealand 865 MHz',
  [REGION_CODE.TH]: 'Thailand',
  [REGION_CODE.LORA_24]: '2.4 GHz',
  [REGION_CODE.UA_433]: 'Ukraine 433 MHz',
  [REGION_CODE.UA_868]: 'Ukraine 868 MHz',
  [REGION_CODE.MY_433]: 'Malaysia 433 MHz',
  [REGION_CODE.MY_919]: 'Malaysia 919 MHz',
  [REGION_CODE.SG_923]: 'Singapore 923 MHz',
}

export const MODEM_PRESET_NAMES: Record<number, string> = {
  [MODEM_PRESET.LONG_FAST]: 'Long Range / Fast',
  [MODEM_PRESET.LONG_SLOW]: 'Long Range / Slow',
  [MODEM_PRESET.VERY_LONG_SLOW]: 'Very Long Range / Slow',
  [MODEM_PRESET.MEDIUM_SLOW]: 'Medium Range / Slow',
  [MODEM_PRESET.MEDIUM_FAST]: 'Medium Range / Fast',
  [MODEM_PRESET.SHORT_SLOW]: 'Short Range / Slow',
  [MODEM_PRESET.SHORT_FAST]: 'Short Range / Fast',
  [MODEM_PRESET.LONG_MODERATE]: 'Long Range / Moderate',
  [MODEM_PRESET.SHORT_TURBO]: 'Short Range / Turbo',
}
